import { Button, Col, Collapse, Form, FormInstance, Input, Row, Select, Space, Spin, Tag } from "antd";
import { NamePath } from "antd/lib/form/interface";
import Axios from "axios";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../../assets/icons/icon";
import CustomCollapse from "../../../components/customCollapse";
import EnumCheckboxes from "../../../components/form/enumCheckboxes";
import InputNum from "../../../components/form/inputNum";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import _t from "../../../lang/translate";
import { getEnumTitleById } from "../../../services/enumService";
import { findMotorAtlasEntries } from "../../../services/motorAtlasService";
import { ModelCatalogFilterForm, ModelMatch, SourceVehiclePreview } from "../../../types/motorAtlasCatalogTypes";
import useMotorAtlasOptions from "./useMotorAtlasOptions";
import { clearDependentFields, mapRecommendedFiltersToForm, ModelBrowserOptions, ModelMatcherInputVehicle } from "./util";
import SwitchInput from "../../../components/form/switchInput";
import DividerLine from "../../../components/lineSplit";

type FiltersProps = {
  setMatches: React.Dispatch<React.SetStateAction<ModelMatch[] | null>>
  sourceVehicle: SourceVehiclePreview | null;
  inputVehicle: ModelMatcherInputVehicle | null,
  presetFiltersRef: React.MutableRefObject<ModelCatalogFilterForm | undefined>

}

export default function MotorAtlasCatalogFilters({ setMatches, sourceVehicle, inputVehicle, presetFiltersRef }: FiltersProps) {
  const reqIdRef = useRef(0)
  const didRenderRef = useRef(false);
  const [form] = Form.useForm<ModelCatalogFilterForm>()
  const [loading, setLoading] = useState(false)
  const {
    handleDrillDown,
    loadingOptionsFields,
    options,
  } = useMotorAtlasOptions("select", false)

  const recommendedFilters = useMemo(() => {
    if (presetFiltersRef.current) {
      return presetFiltersRef.current
    }
    return mapRecommendedFiltersToForm(sourceVehicle?.recommendedFilters ?? null, sourceVehicle?.supplementary?.potentialECodes ?? [])
  }, [sourceVehicle?.recommendedFilters, presetFiltersRef.current])

  useEffect(() => {
    if (didRenderRef.current) return;
    if (!recommendedFilters) return;
    initializeFetches()
    didRenderRef.current = true;
  }, [recommendedFilters])

  async function initializeFetches() {
    applyFilterPipeline(recommendedFilters)
    if (recommendedFilters.type) {
      await handleDrillDown(recommendedFilters, "type")
    }
    if (recommendedFilters.make) {
      await handleDrillDown(recommendedFilters, "make")
    }
    if (recommendedFilters.model) {
      await handleDrillDown(recommendedFilters, "model")
    }
  }

  const handleFetch = useCallback(async (values: ModelCatalogFilterForm) => {
    reqIdRef.current += 1;
    const requestId = reqIdRef.current;
    try {
      setLoading(true)
      const res = await findMotorAtlasEntries(values, inputVehicle?.firstRegDate, false, sourceVehicle?.supplementary?.potentialECodes ?? [])
      setMatches(res.data.data)
    } catch (error) {
      if (!Axios.isCancel(error)) {
        renderErrorMessage(error)
      }
    } finally {
      // Only the latest request can flip loading state back
      if (reqIdRef.current === requestId) {
        setLoading(false)
      }
    }
  }, [])

  const debouncedFetch = useMemo(() => debounce(handleFetch, 300), [handleFetch]);


  useEffect(() => () => {
    debouncedFetch.cancel()
  }, [debouncedFetch])

  function applyFilterPipeline(rawValues: ModelCatalogFilterForm, changedKey?: keyof ModelCatalogFilterForm) {
    let cleaned = rawValues
    if (changedKey) {
      cleaned = clearDependentFields(rawValues, changedKey)
    }
    form.setFieldsValue(cleaned);
    const sanitized = sanitizeRangeFields(cleaned)
    handleDrillDown(sanitized, changedKey)


    const debounceKeys: (keyof ModelCatalogFilterForm)[] = ['year', 'horsePower', 'batteryCapacity', 'engineSize']
    const shouldDebounce = changedKey && debounceKeys.includes(changedKey)
    if (canFetch(sanitized)) {
      if (shouldDebounce) {
        debouncedFetch(sanitized);
      } else {
        handleFetch(sanitized)
      }
    } else {
      setMatches([]);
    }
  }

  function handleValuesChange(changed: Partial<ModelCatalogFilterForm>, all: ModelCatalogFilterForm) {
    const key = Object.keys(changed)[0] as keyof ModelCatalogFilterForm
    applyFilterPipeline(all, key);
    presetFiltersRef.current = all
  }

  function clearField(namePath: NamePath) {
    const obj: any = {};
    let current = obj;
    const path = Array.isArray(namePath) ? [...namePath] : [namePath]
    path.forEach((key, index) => {
      if (index === path.length - 1) {
        current[key] = undefined
      } else {
        current[key] = {}
        current = current[key]
      }
    })
    form.setFieldsValue(obj)
    const values = form.getFieldsValue(true);
    applyFilterPipeline(values);
  }

  return (
    <Form
      form={form}
      labelAlign="left"
      labelCol={{ flex: "0 0 80px" }}
      className="motor_atlas_catalog_mapper_form position-relative"
      size="middle"
      colon={false}
      onValuesChange={handleValuesChange}
      initialValues={recommendedFilters}
    >
      <CustomCollapse defaultActiveKey={!!inputVehicle ? [] : ["1"]} className="ml-0 mr-0">
        <Collapse.Panel forceRender key={"1"} header={
          <div>
            <Spin style={{ position: "absolute", top: 8, right: 17 }} size="default" spinning={loading} />
            <div>
              <span style={{ fontSize: 14 }}>{_t("filters")}</span>
            </div>
            <Form.Item shouldUpdate noStyle>
              {({ getFieldsValue, setFieldsValue }) => {
                const filters = getAppliedFilters(getFieldsValue(), setFieldsValue, options)
                return (
                  <div onClick={e => e.stopPropagation()} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: filters.length ? 16 : 0 }}>
                    {filters?.map((filter) => (
                      <Tag className="ant-tag-exception mr-0 cursor-pointer  hover-bg-light"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          filter.clear()
                          const values = form.getFieldsValue(true);
                          applyFilterPipeline(values, filter.key);
                        }}
                        key={filter.id}>
                        {filter.text}
                      </Tag>
                    ))}
                  </div>
                )
              }}
            </Form.Item>
          </div>
        }
        >
          <Form.Item valuePropName="checked" name="potentialEcodesEnabled"
            labelCol={{ flex: "0 0 100px" }}
            label={"Mulige E-koder"}
          >
            <SwitchInput disabled={!sourceVehicle?.supplementary.potentialECodes?.length}
              label={`(${sourceVehicle?.supplementary.potentialECodes?.length ?? 0} fundet)`} />
          </Form.Item>
          <DividerLine />
          <Row gutter={[8, 8]} wrap={false}>
            <Col flex={"0 0 50%"}>
              <Form.Item name={"type"} label={_t("type")}>
                <Select allowClear options={options.type} />
              </Form.Item>
            </Col>
            <Col flex={"0 0 50%"}>
              <Form.Item label={_t("brand")} name={['make']}>
                <Select loading={loadingOptionsFields.has("make")}
                  disabled={!options.make?.length}
                  showSearch
                  placeholder={_t("choose")}
                  optionFilterProp="label"
                  dropdownMatchSelectWidth={400}
                  allowClear
                  options={options.make} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={_t("model")} name={['model']}>
            <Select loading={loadingOptionsFields.has("model")}
              disabled={!options.model?.length}
              showSearch
              placeholder={_t("choose")}
              optionFilterProp="label"
              dropdownMatchSelectWidth={400}
              allowClear
              options={options.model} />
          </Form.Item>
          <Form.Item label={_t("subModel")} name={['subModel']}>
            <Select loading={loadingOptionsFields.has("subModel")}
              disabled={!options.subModel?.length}
              showSearch
              placeholder={_t("choose")}
              optionFilterProp="label"
              dropdownMatchSelectWidth={400}
              allowClear
              options={options.subModel} />
          </Form.Item>

          <Form.Item label={_t("model_year")}>
            <Space>
              <Form.Item name={['year', 'from']}>
                <Input placeholder={_t("from")}
                  suffix={
                    <Button tabIndex={-1} className="clear-btn"
                      style={{ marginRight: -2 }}
                      onClick={() => clearField(['year', 'from'])}
                      type="text" shape="circle"
                      icon={<Icon size="small" name="close-outline" />}
                    />
                  }
                />
              </Form.Item>
              <Form.Item name={['year', 'to']}>
                <Input placeholder={_t("to")}
                  suffix={
                    <Button tabIndex={-1} className="clear-btn"
                      style={{ marginRight: -2 }}
                      onClick={() => clearField(['year', 'to'])}
                      type="text" shape="circle"
                      icon={<Icon size="small" name="close-outline" />}
                    />
                  }

                />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item label={_t("horse_power_full")}>
            <Space>
              <Form.Item name={['horsePower', 'from']}>
                <InputNum placeholder={_t("from")}
                  suffix={
                    <Button tabIndex={-1} className="clear-btn"
                      onClick={() => clearField(['horsePower', 'from'])}
                      type="text" shape="circle"
                      icon={<Icon size="small" name="close-outline" />}
                    />
                  }

                />
              </Form.Item>
              <Form.Item name={['horsePower', 'to']}>
                <InputNum placeholder={_t("to")}
                  suffix={
                    <Button tabIndex={-1} className="clear-btn"
                      onClick={() => clearField(['horsePower', 'to'])}
                      type="text" shape="circle"
                      icon={<Icon size="small" name="close-outline" />}
                    />
                  }
                />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item label={_t("battery") + " " + _t("battery_unit")}>
            <Space>
              <Form.Item name={['batteryCapacity', 'from']}>
                <InputNum placeholder={_t("from")}
                  suffix={
                    <Button tabIndex={-1} className="clear-btn"
                      onClick={() => clearField(['batteryCapacity', 'from'])}
                      type="text" shape="circle"
                      icon={<Icon size="small" name="close-outline" />}
                    />
                  }
                />
              </Form.Item>
              <Form.Item name={['batteryCapacity', 'to']}>
                <InputNum placeholder={_t("to")}
                  suffix={
                    <Button tabIndex={-1} className="clear-btn"
                      onClick={() => clearField(['batteryCapacity', 'to'])}
                      type="text" shape="circle"
                      icon={<Icon size="small" name="close-outline" />}
                    />
                  }

                />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item label={_t("engine_size_short")}>
            <Space>
              <Form.Item name={['engineSize', 'from']}>
                <InputNum placeholder={_t("from")}
                  suffix={
                    <Button tabIndex={-1} className="clear-btn"
                      onClick={() => clearField(['engineSize', 'from'])}
                      type="text" shape="circle"
                      icon={<Icon size="small" name="close-outline" />}
                    />
                  }
                />
              </Form.Item>
              <Form.Item name={['engineSize', 'to']}>
                <InputNum placeholder={_t("to")}
                  suffix={
                    <Button tabIndex={-1} className="clear-btn"
                      onClick={() => clearField(['engineSize', 'to'])}
                      type="text" shape="circle"
                      icon={<Icon size="small" name="close-outline" />}
                    />
                  }
                />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item name={"fuelType"} label={_t("fuel")}>
            <EnumCheckboxes gridColumns={5} nameKey={['vehicle', 'fuel']} />
          </Form.Item>

          <Form.Item name={"gearbox"} label={_t("gearbox")}>
            <EnumCheckboxes gridColumns={5} nameKey={['vehicle', 'gearbox']} />
          </Form.Item>

          <Form.Item name={"bodyType"} label={_t("trim")}>
            <EnumCheckboxes gridColumns={5} nameKey={['vehicle', 'body']} />
          </Form.Item>

          <Form.Item name={"driveType"} label={_t("drive_wheel")}>
            <EnumCheckboxes gridColumns={5} nameKey={['vehicle', 'driveType']} />
          </Form.Item>
        </Collapse.Panel>
      </CustomCollapse>
    </Form>
  )
}




const THIS_YEAR = new Date().getFullYear();
const LOWEST_SUPPORTED_YEAR = 1960;
const BATTERY_CAPACITY_UPPER_BOUND = 200;
const HORSE_POWER_LOWER_BOUND = 20;
const HORSE_POWER_UPPER_BOUND = 2000;
const ENGINE_SIZE_LOWER_BOUND = 100;
const ENGINE_SIZE_UPPER_BOUND = 9999;
function sanitizeRangeFields(values: ModelCatalogFilterForm) {
  const result = { ...values }
  if (result.year?.from) {
    if (result.year.from < LOWEST_SUPPORTED_YEAR || result.year.from > THIS_YEAR) {
      result.year.from = undefined
    }
  }
  if (result.year?.to) {
    if (result.year.to < LOWEST_SUPPORTED_YEAR || result.year.to > THIS_YEAR) {
      result.year.to = undefined
    }
  }
  if (result.horsePower?.from) {
    if (result.horsePower.from < HORSE_POWER_LOWER_BOUND || result.horsePower.from > HORSE_POWER_UPPER_BOUND) {
      result.horsePower.from = undefined
    }
  }
  if (result.horsePower?.to) {
    if (result.horsePower.to < HORSE_POWER_LOWER_BOUND || result.horsePower.to > HORSE_POWER_UPPER_BOUND) {
      result.horsePower.to = undefined
    }
  }
  if (result.batteryCapacity?.from) {
    if (result.batteryCapacity.from > BATTERY_CAPACITY_UPPER_BOUND) {
      result.batteryCapacity.from = undefined
    }
  }
  if (result.batteryCapacity?.to) {
    if (result.batteryCapacity.to > BATTERY_CAPACITY_UPPER_BOUND) {
      result.batteryCapacity.to = undefined
    }
  }
  if (result.engineSize?.from) {
    if (result.engineSize.from < ENGINE_SIZE_LOWER_BOUND || result.engineSize.from > ENGINE_SIZE_UPPER_BOUND) {
      result.engineSize.from = undefined
    }
  }
  if (result.engineSize?.to) {
    if (result.engineSize.to < ENGINE_SIZE_LOWER_BOUND || result.engineSize.to > ENGINE_SIZE_UPPER_BOUND) {
      result.engineSize.to = undefined
    }
  }


  return result
}

type AppliedFilter = {
  id: string;
  text: string | number;
  key?: keyof ModelCatalogFilterForm,
  clear: () => void;
}


function formatRange(range?: { from?: number, to?: number; }) {
  if (!range) return "";
  const a = range.from
  const b = range.to;
  if (a && b) return `${a}-${b}`;
  if (a) return `${a}+`;
  if (b) return `-${b}`;
  return "";
}

function isEmptyRange(range?: { from?: number; to?: number; }) {
  return !range || (!range?.from || !range?.to)
}

const REQUIRE_YEAR = false;
function canFetch(v: ModelCatalogFilterForm) {
  const baseReady = Boolean(v.type && v.make);
  if (!REQUIRE_YEAR) return baseReady;
  return baseReady && Boolean(v.year?.from && v.year?.to);
}


function getAppliedFilters(rawValues: ModelCatalogFilterForm, setFieldsValue: FormInstance<ModelCatalogFilterForm>['setFieldsValue'], options: ModelBrowserOptions) {
  const filters: AppliedFilter[] = [];
  const values = sanitizeRangeFields(rawValues)

  if (values.potentialEcodesEnabled) {
    filters.push({
      id: `potentialEcodes`,
      text: "Mulige E-koder",
      key: "potentialEcodesEnabled",
      clear: () => setFieldsValue({ potentialEcodesEnabled: false })
    });

    return filters;
  }


  // Simple fields
  if (values.type) {
    filters.push({
      id: `type:${values.type}`,
      text: values.type === "01" ? "Personbil" : "Varebil",
      key: "type",
      clear: () => setFieldsValue({ type: undefined })
    });
  }
  if (values.make) {
    filters.push({
      id: `make:${values.make}`,
      text: options.make.find(item => item.value === values.make)?.label ?? values.make,
      key: "make",
      clear: () => setFieldsValue({ make: undefined })
    });
  }
  if (values.model) {
    filters.push({
      id: `model:${values.model}`,
      text: options.model.find(item => item.value === values.model)?.label ?? values.model,
      key: "model",
      clear: () => setFieldsValue({ model: undefined })
    });
  }
  if (values.subModel) {
    filters.push({
      id: `subModel:${values.subModel}`,
      text: options.subModel.find(item => item.value === values.subModel)?.label ?? values.subModel,
      key: "subModel",
      clear: () => setFieldsValue({ subModel: undefined })
    });
  }

  // Ranges
  if (!isEmptyRange(values?.year)) {
    filters.push({
      id: `year:${formatRange(values?.year)}`,
      text: formatRange(values?.year),
      clear: () => setFieldsValue({ year: { from: undefined, to: undefined } })
    });
  }
  if (!isEmptyRange(values?.horsePower)) {
    filters.push({
      id: `hp:${formatRange(values?.horsePower)}`,
      text: `${formatRange(values?.horsePower)} hk`,
      clear: () => setFieldsValue({ horsePower: { from: undefined, to: undefined } })
    });
  }
  if (!isEmptyRange(values?.batteryCapacity)) {
    filters.push({
      id: `bat:${formatRange(values?.batteryCapacity)}`,
      text: `${formatRange(values?.batteryCapacity)} kWh`,
      clear: () => setFieldsValue({ batteryCapacity: { from: undefined, to: undefined } })
    });
  }
  if (!isEmptyRange(values?.engineSize)) {
    filters.push({
      id: `cc:${formatRange(values?.engineSize)}`,
      text: `${formatRange(values?.engineSize)} cc`,
      clear: () => setFieldsValue({ engineSize: { from: undefined, to: undefined } })
    });
  }

  // Multi-selects → one tag per selected value so users can remove individually
  const multi: (keyof ModelCatalogFilterForm)[] = ["fuelType", "gearbox", "bodyType", "driveType"] as const;
  multi.forEach((key) => {
    const arr = values[key] as number[] | undefined;
    arr?.forEach((val) => {
      let label: string | null = ''
      if (key === 'bodyType') {
        label = getEnumTitleById(val, ["vehicle", "body"])
      } else if (key === "gearbox") {
        label = getEnumTitleById(val, ["vehicle", "gearbox"])
      } else if (key === "driveType") {
        label = getEnumTitleById(val, ["vehicle", "driveType"])
      } else if (key === "fuelType") {
        label = getEnumTitleById(val, ["vehicle", "fuel"])
      }
      filters.push({
        id: `${key}:${val}`,
        text: label ?? '',
        clear: () => {
          const next = (arr || []).filter((x) => x !== val);
          setFieldsValue({ [key]: next.length ? next : undefined });
        }
      });
    });
  })

  return filters
}
